import { useState } from 'react'
import { z } from 'zod'
import { useGetTaskActionQuery } from '../services/taskActionsApi'
import { RepairSubmissionVerdictSchema } from '../guidance/journey-repair/submission-judge-schema'
import { RepairEvidenceSchema } from '../guidance/journey-repair/citations'
import { SupportingEvidence } from './SessionRepairSummary'

const ReviewAction = z.object({ id: z.string(), kind: z.literal('repair.submission-judge') })
const ReviewData = z.object({
  verdict: RepairSubmissionVerdictSchema,
  proposal: z.object({ problem: z.string(), reason: z.string(), changes: z.unknown(), evidence: z.array(RepairEvidenceSchema) }),
})
function Review({ id, index }: { id: string; index: number }) {
  const [open, setOpen] = useState(false)
  const query = useGetTaskActionQuery(id, { skip: !open })
  const parsed = ReviewData.safeParse(query.data?.data)
  return <details onToggle={event => setOpen(event.currentTarget.open)}>
    <summary>Repair submission review {index + 1}</summary>
    {query.isLoading && <p>Loading review…</p>}
    {query.isError && <p>Could not load the review.</p>}
    {query.data && !parsed.success && <p>Review results are unavailable.</p>}
    {parsed.success && <>
      <p><strong>{parsed.data.verdict.inconclusive ? 'Inconclusive' : parsed.data.verdict.approved ? 'Approved for retry' : 'Rejected'}</strong> — {parsed.data.verdict.assessment}</p>
      <ul>{parsed.data.verdict.problems.map((problem, i) => <li key={i}>{problem}</li>)}</ul>
      <p><strong>Problem:</strong> {parsed.data.proposal.problem}</p>
      <p><strong>Repair:</strong> {parsed.data.proposal.reason}</p>
      <SupportingEvidence evidence={parsed.data.proposal.evidence} />
      <details><summary>Submitted changes</summary><pre>{JSON.stringify(parsed.data.proposal.changes, null, 2)}</pre></details>
      <a href={`#action-${id}`}>Action and model call</a>
    </>}
  </details>
}
export default function RepairSubmissionReviews({ actions }: { actions: unknown[] | undefined }) {
  const reviews = (actions ?? []).flatMap(action => {
    const parsed = ReviewAction.safeParse(action)
    return parsed.success ? [parsed.data] : []
  })
  if (!reviews.length) return null
  return <section><h2>Repair submission reviews</h2>{reviews.map((review, index) => <Review key={review.id} id={review.id} index={index} />)}</section>
}
